import { Component, computed, OnInit } from '@angular/core';
import { BaseFormControlComponent } from '../../../../classes/base-form-control.component';
import { AsyncPipe, NgStyle } from '@angular/common';
import { TrnExamQuestion } from '../../../../../../../../models/ts/trn-exam-question.model';
import { TranslatePipe } from '../../../../../../../shared/pipes/translate/translate.pipe';
import { BehaviorSubject, combineLatest, filter, lastValueFrom, map, Observable, of, switchMap, take, takeUntil, takeWhile, tap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UpdateDeleteState } from '../../../../../../../../models/ts/update-delete-state.model';
import { SafePipe } from 'safe-pipe';
import { IconComponent } from '../../../../../../../shared/components/ui/icon/icon.component';
import { IconLibrary } from 'src/models/ts/icon-library.model';
import { TrainingApiService } from '../../../../../../../api/bizzmine/training/training-api.service';
import { Dialog } from '@angular/cdk/dialog';
import { ExamCreatorAddQuestionControlComponent } from '../exam-creator-add-question-control/exam-creator-add-question-control/exam-creator-add-question-control.component';
import { LoaderButtonComponent } from '../../../../../../../shared/components/loader-button/loader-button/loader-button.component';
import { ExamCreatorGenerateAiControlComponent } from '../exam-creator-ai-control-component/exam-creator-generate-ai-control.component';
import { ProtectedFieldType } from '../../../../../../../../models/ts/protected-field-type.model';
import {
  selectForm,
  selectFormFieldValues,
  selectFormLockState
} from '../../../../../../../store/features/forms/forms-selectors';
import { formsActions } from '../../../../../../../store/features/forms/forms-actions';
import { ExamGenerationIntent } from '../../../../../../../../models/ts/exam-generation-intent';
import { LicenseKeyType } from '../../../../../../../../models/ts/license-key-type.model';
import { ExamQuestionSignalr } from 'src/models/ts/exam-question-signalr';
import { SignalRService } from 'src/app/core/signalR/signal-r.service';
import { ExamQuestionGeneratedSignalrMessage } from 'src/models/ts/exam-question-generated-signalr-message';
import { ExamGenerationCompletedSignalRMessage } from 'src/models/ts/exam-question-generation-complete-signalr-message';
import { ChatResultStatus } from 'src/models/ts/chat-result-status';
import { AlertService } from 'src/app/features/bizzmine/alerts/alert.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { AiFirstTimeDialogComponent } from 'src/app/shared/components/ai-first-time-dialog/ai-first-time-dialog.component';
import { PopUpModalType } from 'src/app/shared/enums/popup-modal-type';
import { AiApiService } from 'src/app/api/bizzmine/ai/ai-api.service';

@Component({
  selector: 'bizz-exam-creator-control',
  standalone: true,
  imports: [
    TranslatePipe,
    FormsModule,
    AsyncPipe,
    SafePipe,
    NgStyle,
    IconComponent,
    LoaderButtonComponent
  ],
  templateUrl: './exam-creator-control.component.html',
  styleUrl: './exam-creator-control.component.scss'
})
export class ExamCreatorControlComponent extends BaseFormControlComponent implements OnInit {
  public isLoadingAi = false;
  public intent: ExamGenerationIntent | undefined = undefined;
  public questionOvservables: Array<Observable<Array<TrnExamQuestion>>> = [];
  public isLocked = computed(() => {
    if (this.formId != null) {
      return this.store$.selectSignal(selectFormLockState(this.formId))();
    }
    return false;
  });
  public searchTerm$ = new BehaviorSubject<string>('');
  public hasAiLicense$ = this.trainingApiService.hasAiLicense(LicenseKeyType.AIExams).pipe(
    take(1)
  );

  public showAiButton$ = this.hasAiLicense$.pipe(map(hasLicense => hasLicense && this.formFieldSignal()?.HasDocumentLinked));
  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected readonly UpdateDeleteState = UpdateDeleteState;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected readonly IconLibrary = IconLibrary;
  private questions: Array<TrnExamQuestion>;
  public questions2: Array<ExamQuestionSignalr>;
  public questions$ = this.searchTerm$
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      map(search => {
        return this.applyFilter(this.questions2, search)
          .map((item) => ({
            ...item,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            IsExpanded: false
          }));
      }));
  private collectionsId: number;
  private versionId: number;

  public constructor(private trainingApiService: TrainingApiService, private aiAPiService: AiApiService,
                     private dialog: Dialog, private signalRService: SignalRService, private alertService: AlertService, private translationService: TranslationService ) {
    super();
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    const cloned = structuredClone(this.formFieldSignal());
    /*this.questions2  = <Array<TrnExamQuestion>>cloned?.Value;*/
    this.questions2 = cloned?.Value.map((q: TrnExamQuestion) => ({
      id: q.ID ?? 0,
      state: q.State ?? UpdateDeleteState.Update,
      caption: q.Caption,
      isRequired: q.IsRequired,
      submittedAnswerID: q.SubmittedAnswerID,
      answers: q.Answers.map(a => ({
        id: a.ID ?? 0,
        caption: a.Caption,
        score: a.Score,
        state: a.State ?? UpdateDeleteState.Update
      }))
    }));
    this.formField$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(field => {
      this.questions2 = field?.Value.map((q: TrnExamQuestion) => ({
        id: q.ID ?? 0,
        state: q.State ?? UpdateDeleteState.Update,
        caption: q.Caption,
        isRequired: q.IsRequired,
        submittedAnswerID: q.SubmittedAnswerID,
        answers: q.Answers.map(a => ({
          id: a.ID ?? 0,
          caption: a.Caption,
          score: a.Score,
          state: a.State ?? UpdateDeleteState.Update
        }))
      }));
    });
  }

  public addQuestion(): void {
    this.editQuestion(undefined);
  }

  public generateWithAi(): void {
    let maxQuestion: number | null = null;
    let maxAnswers: number | null = null;
    let examName: string | null = null;

    if (!this.collectionsId || !this.versionId) {
      this.store$.select(selectForm(this.formId)).pipe(take(1)).subscribe(form => {
        if (form && form.data) {
          this.collectionsId = form.data.CollectionsID;
          this.versionId = form.data.VersionsID;
        }
      });
    }

    this.store$.select(selectFormFieldValues(this.formId, { collectionId: this.collectionsId }, [ProtectedFieldType.ExamAmountOfQuestions, ProtectedFieldType.ExamAmountOfAnswersPerQuestion, ProtectedFieldType.ExamName]))
      .pipe(
        take(1)
      ).subscribe(fieldValues => {
        if (fieldValues) {
          maxQuestion = fieldValues[ProtectedFieldType.ExamAmountOfQuestions] as number;
          maxAnswers = fieldValues[ProtectedFieldType.ExamAmountOfAnswersPerQuestion] as number;
          examName = fieldValues[ProtectedFieldType.ExamName] as string;
        }
      }
    );

    this.dialog.open<{
      questions: number,
      answersPerQuestion: number
    }>(ExamCreatorGenerateAiControlComponent, {
      data: {
        defaultQuestions: maxQuestion,
        defaultAnswersPerQuestion: maxAnswers
      }
    }).closed.pipe(take(1)).subscribe((result) => {
      const currentQuestions = this.questions?.map(q => q.Caption) ?? [];
      if (result != null && result.answersPerQuestion > 0 && result.questions > 0) {
        this.invokeGenerateWithAiIntent({
          AmountOfQuestions: result.questions,
          AmountOfAnswers: result.answersPerQuestion,
          CollectionsID: this.collectionsId,
          ExamVersionsID: this.versionId,
          PreviousQuestionsList: currentQuestions,
          Subject: examName ?? ''
        });
      }
    });
  }

  public deleteQuestion(index: number): void {
    this.questions2[index].state = UpdateDeleteState.Delete;
    this.searchTerm$.next(this.searchTerm$.value);
    this.dispatchValueChange();
  }

  private dispatchValueChange(): void {
    const field = this.formFieldSignal();
    
    const backendControl = this.questions2.map(q => ({
      ID: q.id ?? 0,
      State: q.state ?? UpdateDeleteState.Update,
      Caption: q.caption,
      IsRequired: q.isRequired,
      SubmittedAnswerID: 0,
      Answers: q.answers.map(a => ({
        ID: a.id ?? 0,
        Caption: a.caption,
        Score: a.score,
        State: a.state ?? UpdateDeleteState.Update
      }))
    }));

    if (field) {
      this.store$.dispatch(formsActions.updateFormField({
        formId: this.formId, fieldId: field.Id, value: backendControl
      }));
    }
  }

  public editQuestion(index: number | undefined): void {
    const question = index != null ? this.questions2[index] : null;
    lastValueFrom(this.dialog.open<ExamQuestionSignalr>(ExamCreatorAddQuestionControlComponent, {
      data: question
    }).closed).then((result) => {
      if (result != null) {
        if (index == null) {
          this.questions2.push(result);
        } else {
          result.Generated = false;
          result.state = UpdateDeleteState.Update;
          if (result.answers && result.answers.length > 0) {
            result.answers.forEach((item) => {
              item.state = UpdateDeleteState.Update;
            });
          }
          this.questions2[index] = result;
        }
        this.searchTerm$.next(this.searchTerm$.value);
      }
      this.dispatchValueChange();
    });
  }

  public generatingQuestionQueue: Array<{
    Failed?: boolean | undefined,
    Success?: boolean | undefined,
  }> = [];

  public getFailedGenerates(): number {
    return this.generatingQuestionQueue.filter((item) => item.Failed === true).length;
  }

  public getSuccessfulGenerates(): number {
    return this.generatingQuestionQueue.filter((item) => item.Success === true).length;
  }

  public i: number;
  private conversationId$ = new BehaviorSubject<string | null>(null);
  private chatResult$ = this.signalRService
    .filterByType(ExamQuestionGeneratedSignalrMessage, 'ExamQuestionGeneratedSignalRMessage')
    .pipe(
      takeWhile(() => this.conversationId$.value != null),
      takeUntilDestroyed(this.destroyRef),
      filter((result) => this.conversationId$.value != null),
      tap((result) => {
        if (result.question != null) {
          this.generatingQuestionQueue[this.i] = { Success: true };
          this.i++;
          this.submitAnswer(result);
        }else {
          this.generatingQuestionQueue[this.i] = { Failed: true };
          this.i++;
        }
      })
    );

  private generationSucces$ = this.signalRService
    .filterByType(ExamGenerationCompletedSignalRMessage, 'ExamGenerationCompletedSignalRMessage')
    .pipe(
      takeWhile(() => this.conversationId$.value != null),
      takeUntilDestroyed(this.destroyRef),
      tap((result) => {
        this.conversationId$.next(null);
        this.isLoadingAi = false;
        if (result.status == ChatResultStatus.SUCCESS) {
          this.dispatchValueChange();
        } else {
          //CHeck if the result has a popUpModalType, if so we need to show the dialog
          if (result.popUpModalType) {
            const data = {
              PopUpModalType: result.popUpModalType,
              PopUpModelText: result.popUpModelText,
              PopupModelMessage: result.popupModelMessage,
              UsageMeterType: result.usageMeterType
            }
            this.dialog.open<{ error: { error: any } }>(AiFirstTimeDialogComponent, {
              data: {
                isexamai: true,
                intent: this.intent,
                error: { error: data }
              }
            }).closed.pipe(take(1)).subscribe((result) => {
              //CHeck if we need to restart the signalR loop
              if(result){
                this.invokeGenerateWithAiIntent(this.intent!);
              }
            });
          }else{
            this.alertService.setAlert({
              type: 'error',
              content: [],
              title: this.translationService.translate('BadRequest'),
              icon: 'triangle-exclamation',
              dismissable: true,
              timer: 5000
            });
          }
        }
      }));
  private submitAnswer(result: ExamQuestionGeneratedSignalrMessage): void {
    this.questions2 = this.questions2 ?? [];
    this.questions2.push(result.question); // properties are readonly -> need to clone
    this.searchTerm$.next('');
  }

  private invokeGenerateWithAiIntent(intent: ExamGenerationIntent): void {
    this.intent = intent;
    this.isLoadingAi = true;
    this.generatingQuestionQueue = [];
    
    if(intent.AmountOfQuestions) {
      this.trainingApiService.getGeneratedQuestionsWithAi2(intent).subscribe({
        next: (connectionId) => {
           this.i = 0;
           this.conversationId$.next(connectionId);
           this.chatResult$.pipe(
             takeUntil(
               this.conversationId$.pipe(
                 filter(id => id == null)
               )
             )
           ).subscribe();
           this.generationSucces$.pipe(
             takeUntil(
               this.conversationId$.pipe(
                 filter(id => id == null)
               )
             )
           ).subscribe();
        },
        error: () => {
          this.isLoadingAi = false;
        }
      })
    }
  }

  private applyFilter(source: Array<ExamQuestionSignalr>, searchTerm: string): Array<ExamQuestionSignalr> {
    return source?.filter((item) => {
      return item.caption?.toLowerCase()?.includes(searchTerm.toLowerCase());
    }) ?? [];
  }

  protected override focus(): void {
    // Add your implementation here
  }
}
